import React from 'react';
import { FaBalanceScale, FaExclamationTriangle, FaFileContract, FaUserTie, FaShieldAlt, FaChartLine, FaCertificate, FaLock } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ComplianceDisclaimer = ({ contentData }) => {
  return (
    <section className="relative py-24 bg-white overflow-hidden" id='disclosures'>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Scale Icons */}
        <motion.div
          animate={{ 
            x: [0, -25, 0],
            y: [0, 15, 0],
            rotate: [0, -3, 3, 0]
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute top-20 left-10 text-6xl opacity-8 text-blue-400"
          style={{fontSize: "170px"}}
        >
          <FaBalanceScale />
        </motion.div>
        
        <motion.div
          animate={{ 
            x: [0, -25, 0],
            y: [0, 15, 0],
            rotate: [0, -3, 3, 0]
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute top-20 right-10 text-7xl opacity-5 text-purple-400"
          style={{fontSize: "170px"}}
        >
          <FaShieldAlt />
        </motion.div>
        
        <motion.div
          animate={{ 
            x: [0, 20, 0],
            y: [0, -15, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{ 
            duration: 9, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-20 left-10 text-5xl opacity-5 text-emerald-400"
        >
          <FaCertificate />
        </motion.div>

        {/* Floating Documents */}
        <motion.div
          animate={{ 
            x: [0, -30, 0],
            y: [0, 25, 0],
            rotate: [0, -8, 8, 0]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
          className="absolute bottom-10 right-20 text-6xl opacity-5 text-amber-400"
        >
          <FaFileContract />
        </motion.div>

        {/* Animated Dots Pattern */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            animate={{ 
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{ 
              duration: 2 + Math.random() * 2, 
              repeat: Infinity,
              delay: i * 0.1
            }}
            className="absolute w-1.5 h-1.5 bg-blue-300 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}

        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-3" style={{
          backgroundImage: `linear-gradient(90deg, #3b82f6 1px, transparent 1px),
                           linear-gradient(180deg, #3b82f6 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50 px-8 py-3 rounded-full mb-6 border border-blue-200/50 shadow-sm"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <FaBalanceScale className="text-blue-600" />
            </motion.div>
            <span className="font-bold text-gray-900 tracking-wide">REGULATORY COMPLIANCE</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
           Legal Framework & Compliance Disclosure
            
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Transparency and regulatory adherence are at the core of our operations
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Enhanced SEBI Registration Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            whileHover={{ y: -5 }}
            viewport={{ once: true }}
            className="relative group"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-blue-400 rounded-2xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className="relative bg-gradient-to-br from-white via-blue-50/30 to-white p-8 rounded-2xl border border-blue-100 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm">
              <div className="flex items-center gap-4 mb-6">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg"
                >
                  <FaBalanceScale className="text-white text-xl" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{contentData?.ra_left_card_title_main}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <FaLock className="text-blue-500 text-sm" />
                    <p className="text-blue-600 font-semibold text-lg">{contentData?.ra_left_card_info_main}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-blue-200/30"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <FaUserTie className="text-blue-500" />
                    <span className="font-bold text-gray-900">{contentData?.ra_left_card_info_main}</span>
                  </div>
                  <p className="text-gray-800">{contentData?.ra_left_card_content1}</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-blue-200/30"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <FaChartLine className="text-blue-500" />
                    <span className="font-bold text-gray-900">{contentData?.ra_left_card_title2}</span>
                  </div>
                  <p className="text-gray-800 leading-relaxed">
                    {contentData?.ra_left_card_content2}
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Risk Disclosure Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            whileHover={{ y: -5 }}
            viewport={{ once: true }}
            className="relative group"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-red-400/20 to-orange-400/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className="relative bg-gradient-to-br from-white via-red-50/20 to-white p-8 rounded-2xl border border-red-100 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm min-h-100">
              <div className="flex items-center gap-4 mb-6">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg"
                >
                  <FaExclamationTriangle className="text-white text-xl" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Risk Disclosure</h3>
                  <p className="text-red-600 font-semibold">Important Investor Information</p>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
                className="p-5 bg-gradient-to-r from-red-50/50 to-orange-50/50 rounded-xl border border-red-200/50"
              >
                <div className="flex items-center gap-3 mb-4">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-2xl"
                  >
                    ⚠️
                  </motion.div>
                  <p className="text-red-700 font-bold text-lg">
                    {contentData?.ra_rd_title}
                  </p>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {contentData?.ra_rd_info}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Enhanced Disclaimer Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          whileHover={{ y: -3 }}
          viewport={{ once: true }}
          className="relative group mb-8"
        >
          {/* Glow Effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-400/20 to-yellow-400/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          <div className="relative bg-gradient-to-br from-white via-amber-50/30 to-white p-8 rounded-2xl border border-amber-100 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-6">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg"
              >
                <FaFileContract className="text-white text-xl" />
              </motion.div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Disclaimer & Responsibility</h3>
                <p className="text-amber-600 font-semibold">Important Legal Information</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                viewport={{ once: true }}
                className="p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-amber-200/30"
              >
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <FaShieldAlt className="text-amber-500" />
                  {contentData?.ra_dr_title1}
                </h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {contentData?.ra_dr_content1}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                viewport={{ once: true }}
                className="p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-amber-200/30"
              >
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <FaUserTie className="text-amber-500" />
                  {contentData?.ra_dr_title2}
                </h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {contentData?.ra_dr_content2}
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Compliance Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4"
        >
          {[
            { text: contentData?.ra_tag1, color: "from-blue-500 to-blue-600", icon: <FaShieldAlt /> },
            { text: contentData?.ra_tag2, color: "from-emerald-500 to-emerald-600", icon: <FaCertificate /> },
            { text: contentData?.ra_tag3, color: "from-purple-500 to-purple-600", icon: <FaBalanceScale /> },
            { text: contentData?.ra_tag4, color: "from-amber-500 to-amber-600", icon: <FaFileContract /> }
          ].map((badge, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.05, y: -2 }}
              className={`px-6 py-3 bg-gradient-to-r ${badge.color} text-white rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2`}
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: index * 0.2 }}
              >
                {badge.icon}
              </motion.div>
              <span>{badge.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ComplianceDisclaimer;